import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import cfg from '../config';
import {
    FaHome, FaBell, FaRoad, FaUser, FaBars,
    FaSignOutAlt, FaQrcode, FaTools, FaDownload,
    FaExchangeAlt, FaCalendarAlt, FaTshirt, FaChartBar,
    FaCogs, FaPuzzlePiece, FaFileAlt, FaUserFriends, FaBullhorn,
    FaVoteYea, FaClipboardList, FaWpforms, FaKey,
} from 'react-icons/fa';
import api from './axiosSetup';
import { useApp } from '../AppContext';
import './css/BottomNav.css';

const BottomNav = () => {
    const { isOn } = useApp();
    const navigate = useNavigate();
    const location = useLocation();
    const [isZunftrat, setIsZunftrat] = useState(false);
    const [isAppAdmin, setIsAppAdmin] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const fetchUnreadCount = useCallback(() => {
        api.get('/api/notifications/unread-count')
            .then(r => setUnreadCount(r.data.count || 0))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const token = localStorage.getItem('authToken');
        const headers = { Authorization: `Bearer ${token}` };

        api.get('/api/navbar-zunftrat', { headers })
            .then(r => setIsZunftrat(r.data.isZunftrat === 1))
            .catch(() => {});

        api.get('/api/navbar-admin', { headers })
            .then(r => setIsAppAdmin(r.data.isAdmin === 1))
            .catch(() => {});

        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 60 * 1000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    useEffect(() => {
        setDrawerOpen(false);
    }, [location.pathname]);

    const handleLogout = async () => {
        const refreshToken = localStorage.getItem('refreshToken');
        try {
            await api.post('/api/logout', { refreshToken });
        } catch {}
        localStorage.removeItem('authToken');
        localStorage.removeItem('refreshToken');
        navigate('/login', { state: { message: 'Du wurdest erfolgreich abgemeldet.' } });
    };

    const DrawerLink = ({ to, icon: Icon, children }) => (
        <NavLink
            to={to}
            className={({ isActive }) => `bn-drawer-item${isActive ? ' active' : ''}`}
        >
            <Icon />
            <span>{children}</span>
        </NavLink>
    );

    return (
        <>
            {drawerOpen && (
                <div className="bn-overlay" onClick={() => setDrawerOpen(false)} />
            )}

            <div className={`bn-drawer${drawerOpen ? ' bn-drawer--open' : ''}`}>
                <div className="bn-drawer-handle-row" onClick={() => setDrawerOpen(false)}>
                    <div className="bn-drawer-handle" />
                </div>
                <div className="bn-drawer-scroll">
                    <div className="bn-drawer-section">
                        <div className="bn-drawer-section-title">Allgemein</div>
                        <DrawerLink to="/changelog" icon={FaBullhorn}>Was ist neu?</DrawerLink>
                        {/* Reihenfolge nach Anlass: erst die eigenen Einsaetze,
                            dann die Anliegen rund ums Haes, dann Mitbestimmung,
                            zuletzt Nachschlagen. */}
                        {isOn('Modul_Arbeitseinsatz') && (
                            <DrawerLink to="/meine-einsaetze" icon={FaTools}>Meine Arbeitseinsätze</DrawerLink>
                        )}
                        {isOn('Modul_Arbeitseinsatz') && (
                            <DrawerLink to="/swap-boerse" icon={FaExchangeAlt}>Tauschbörse</DrawerLink>
                        )}
                        {isOn('Modul_Haes') && (
                            <DrawerLink to="/service-apply-haes" icon={FaTshirt}>{cfg.costumeTerm} beantragen</DrawerLink>
                        )}
                        {isOn('Modul_Haes') && (
                            <DrawerLink to="/service-order-spare-parts" icon={FaTshirt}>{cfg.costumeTerm}-Einzelteile bestellen</DrawerLink>
                        )}
                        <DrawerLink to="/abstimmungen" icon={FaVoteYea}>Abstimmungen</DrawerLink>
                        {isOn('Modul_Tasks') && (
                            <DrawerLink to="/meine-aufgaben" icon={FaClipboardList}>Meine Aufgaben</DrawerLink>
                        )}
                        <DrawerLink to="/downloads" icon={FaDownload}>Dateien-Downloads</DrawerLink>
                        {isOn('Modul_Veranstaltungen') && (
                            <DrawerLink to="/my-qr-code" icon={FaQrcode}>Mein QR-Code</DrawerLink>
                        )}
                    </div>

                    {isZunftrat && (
                        <div className="bn-drawer-section">
                            <div className="bn-drawer-section-title bn-drawer-section-title--zr">{cfg.adminRoleName}</div>
                            {/* Der Zusatz "(ZR)" stand frueher hinter einigen
                                Punkten. In einem Abschnitt, der ohnehin nur der
                                Bossebene gehoert, sagt er nichts aus. */}
                            <DrawerLink to="/zr-notification" icon={FaBell}>Mitteilungen</DrawerLink>
                            <DrawerLink to="/zr-member" icon={FaUserFriends}>{cfg.memberTermPlural} verwalten</DrawerLink>

                            {isOn('Modul_Veranstaltungen') && (
                                <DrawerLink to="/zr-moves" icon={FaRoad}>{cfg.eventTermPlural} verwalten</DrawerLink>
                            )}
                            {isOn('Modul_Veranstaltungen') && (
                                <DrawerLink to="/zr-calendar" icon={FaCalendarAlt}>Kalender</DrawerLink>
                            )}
                            {isOn('Modul_Veranstaltungen') && (
                                <DrawerLink to="/zr-qr-reader" icon={FaQrcode}>QR-Codes bestätigen</DrawerLink>
                            )}

                            {isOn('Modul_Haes') && (
                                <DrawerLink to="/zr-haes" icon={FaTshirt}>{cfg.costumeTermPlural} verwalten</DrawerLink>
                            )}
                            {isOn('Modul_Haes') && (
                                <DrawerLink to="/zr-costume-type-managers" icon={FaTshirt}>{cfg.costumeTerm}typen &amp; Manager</DrawerLink>
                            )}

                            {isOn('Modul_Arbeitseinsatz') && (
                                <DrawerLink to="/zr-workassignment" icon={FaTools}>Arbeitseinsätze verwalten</DrawerLink>
                            )}
                            {isOn('Modul_Arbeitseinsatz') && (
                                <DrawerLink to="/zr-swap-boerse" icon={FaExchangeAlt}>Tauschbörse prüfen</DrawerLink>
                            )}

                            <DrawerLink to="/zr-polls" icon={FaVoteYea}>Abstimmungen verwalten</DrawerLink>
                            {isOn('Modul_Tasks') && (
                                <DrawerLink to="/zr-aufgaben" icon={FaClipboardList}>Aufgabenverwaltung</DrawerLink>
                            )}

                            <DrawerLink to="/zr-download" icon={FaFileAlt}>Dokumente bereitstellen</DrawerLink>
                            {isOn('Modul_Berichte') && (
                                <DrawerLink to="/zr-statistics" icon={FaChartBar}>Statistiken</DrawerLink>
                            )}
                            <DrawerLink to="/zr-audit-log" icon={FaFileAlt}>Aktions-Log (DSGVO)</DrawerLink>
                            <DrawerLink to="/zr-license" icon={FaKey}>Lizenz</DrawerLink>
                        </div>
                    )}

                    {isAppAdmin && (
                        <div className="bn-drawer-section">
                            <div className="bn-drawer-section-title bn-drawer-section-title--admin">Admin</div>
                            <DrawerLink to="/admin-analytics" icon={FaChartBar}>Analytik &amp; System</DrawerLink>
                            <DrawerLink to="/admin-features" icon={FaPuzzlePiece}>Feature-Verwaltung</DrawerLink>
                            <DrawerLink to="/admin-config" icon={FaCogs}>App-Konfiguration</DrawerLink>
                            <DrawerLink to="/admin-forms" icon={FaWpforms}>Formulare gestalten</DrawerLink>
                            
                            <DrawerLink to="/admin-changelog" icon={FaBullhorn}>Changelog</DrawerLink>
                            <DrawerLink to="/admin-qr-scan-log" icon={FaQrcode}>QR-Code Audit-Log</DrawerLink>
                        </div>
                    )}

                    <div className="bn-drawer-section">
                        <button className="bn-drawer-item bn-drawer-item--logout" onClick={handleLogout}>
                            <FaSignOutAlt />
                            <span>Abmelden</span>
                        </button>
                    </div>
                </div>
            </div>

            <nav className="bottom-nav d-flex d-lg-none">
                <NavLink
                    to="/dashboard"
                    className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
                >
                    <FaHome className="bottom-nav-icon" />
                    <span>Start</span>
                </NavLink>

                <NavLink
                    to="/notifications"
                    className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
                    onClick={() => setUnreadCount(0)}
                >
                    <span className="position-relative">
                        <FaBell className="bottom-nav-icon" />
                        {unreadCount > 0 && (
                            <span
                                className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                                style={{ fontSize: '0.6rem', padding: '0.2em 0.4em' }}
                            >
                                {unreadCount > 9 ? '9+' : unreadCount}
                            </span>
                        )}
                    </span>
                    <span>News</span>
                </NavLink>

                {isOn('Modul_Veranstaltungen') ? (
                    <NavLink
                        to="/move-table"
                        className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
                    >
                        <FaRoad className="bottom-nav-icon" />
                        <span>{cfg.eventTermPlural}</span>
                    </NavLink>
                ) : isOn('Modul_Haes') ? (
                    // Ersatzplatz in der unteren Leiste, wenn es keine
                    // Veranstaltungen gibt. Frueher stand hier "Service" -
                    // eine Seite, die dieselben Uebersichten zeigte wie das
                    // Profil direkt daneben.
                    <NavLink
                        to="/service-apply-haes"
                        className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
                    >
                        <FaTshirt className="bottom-nav-icon" />
                        <span>{cfg.costumeTerm}</span>
                    </NavLink>
                ) : (
                    <NavLink
                        to="/downloads"
                        className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
                    >
                        <FaDownload className="bottom-nav-icon" />
                        <span>Downloads</span>
                    </NavLink>
                )}

                <NavLink
                    to="/meine-daten"
                    className={({ isActive }) => `bottom-nav-item${isActive ? ' active' : ''}`}
                >
                    <FaUser className="bottom-nav-icon" />
                    <span>Profil</span>
                </NavLink>

                <button
                    className={`bottom-nav-item${drawerOpen ? ' active' : ''}`}
                    onClick={() => setDrawerOpen(v => !v)}
                >
                    <FaBars className="bottom-nav-icon" />
                    <span>Mehr</span>
                </button>
            </nav>
        </>
    );
};

export default BottomNav;
